import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { DailyLog } from "../types";
import { commonTextFieldProps } from "../../../utils/styles";
import { staffApi } from "../../../services/api";
import { fixedIncomeApi } from "../apiService";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";

interface LogActionModalProps {
  open: boolean;
  onClose: () => void;
  editingLog: DailyLog | null;
  formData: Partial<DailyLog>;
  fixedJourneyId: string;
  getDailyLogs: () => void;
}

const validationSchema = Yup.object().shape({
  tripStartOdometer: Yup.number()
    .required("Trip start odometer is required")
    .min(0, "Trip start odometer must be positive"),
  tripEndOdometer: Yup.number()
    .required("Trip end odometer is required")
    .min(0, "Trip end odometer must be positive")
    .test(
      "is-greater-than-start",
      "Trip end odometer must be greater than start odometer",
      function (value) {
        const { tripStartOdometer } = this.parent;
        return !value || !tripStartOdometer || value > tripStartOdometer;
      }
    ),
  driverName: Yup.string().required("Driver name is required"),
  tripStartTime: Yup.string().required("Trip start time is required"),
  tripEndTime: Yup.string().required("Trip end time is required"),
  numberOfTrips: Yup.number()
    .required("Number of trips is required")
    .min(1, "Number of trips must be at least 1"),
  startPoint: Yup.string().required("Start point is required"),
  endPoint: Yup.string().required("End point is required"),
  remarks: Yup.string().optional().nullable().notRequired(),
});

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const LogActionModal: React.FC<LogActionModalProps> = ({
  open,
  onClose,
  editingLog,
  formData,
  fixedJourneyId,
  getDailyLogs,
}) => {
  const [staffOptions, setStaffOptions] = useState<any[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { showToast } = useToast();

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Search staff data based on category
  const searchStaffData = useCallback(async (term: string) => {
    if (term.length < 2 && term.length > 0) return;

    setIsLoading(true);
    setSearchError("");

    try {
      let data: any[] = await staffApi.searchDrivers(term);
      setStaffOptions(data);
    } catch (error) {
      setSearchError(`Failed to load Driver data`);
      console.error(`Error searching Driver:`, error);
      setStaffOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to search when debounced term or category changes
  useEffect(() => {
    searchStaffData(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchStaffData]);

  // Initialize state from form values (for edit mode)
  useEffect(() => {
    // Try to find and set selected staff if referenceId exists
    if (formData?.driverName && staffOptions?.length > 0) {
      const staff = staffOptions.find((s) => s?.name === formData?.driverName);
      if (staff) {
        setSelectedStaff(staff);
      }
    }
  }, [formData, staffOptions]);

  // Handle staff selection
  const handleStaffChange = (
    staff: any | null,
    setFieldValue: any,
    inputValue?: string
  ) => {
    //set isEdited to true
    setFieldValue(`driverName`, staff.name);
    setSelectedStaff(staff);

    if (staff) {
      // Auto-fill fields from master data
      setFieldValue(`driverName`, staff.name);
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode
      setFieldValue(`driverName`, inputValue.trim());
    } else {
      // Clear fields
      setFieldValue(`driverName`, "");
    }
  };

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return `${option.name} - ${option.contactNumber}`;
  };

  // Render staff option
  const renderStaffOption = (props: any, option: any) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        {option.contactNumber && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem", mt: 0.25 }}
          >
            Contact: {option.contactNumber}
          </Typography>
        )}
        <Chip
          label={`License: ${option.drivingLicense}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mt: 0.5 }}
        />
      </Box>
    </Box>
  );

  const handleSubmit = async (
    values: Partial<DailyLog>,
    setFieldValue: any,
    isContinue?: boolean
  ) => {
    if (!isContinue) {
      setIsSubmitting(true);
    }
    if (editingLog) {
      let promises: any[] = [];
      promises.push(fixedIncomeApi.updateDailyLog(editingLog.id!, values));
      const { id, ...restValues } = formData;
      promises.push(
        fixedIncomeApi.softDeleteDailyLog(fixedJourneyId, restValues)
      );
      const responses = await Promise.all(promises);
      if (responses[0] && responses[1]) {
        showToast("Log updated successfully", "success");
        getDailyLogs();
        setIsSubmitting(false);
        onClose();
      }
    } else {
      const response = await fixedIncomeApi.createDailyLog(
        values,
        fixedJourneyId
      );
      if (response) {
        showToast("Log added successfully", "success");
        if (isContinue) {
          setFieldValue("startPoint", values.startPoint);
          setFieldValue("endPoint", values.endPoint);
          setFieldValue("driverName", values.driverName);
          setFieldValue("numberOfTrips", values.numberOfTrips);
          setFieldValue("dateOfEntry", values.dateOfEntry);
          setFieldValue("tripStartOdometer", 0);
          setFieldValue("tripEndOdometer", 0);
          setFieldValue("tripStartTime", "");
          setFieldValue("tripEndTime", "");
        } else {
          setIsSubmitting(false);
          getDailyLogs();
          setSelectedStaff(null);
          onClose();
        }
      }
    }
  };

  if (isSubmitting) {
    return <Loading message="Submitting..." fullScreen={false} />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>
        {editingLog ? "Edit Log" : "Add New Log"}
      </DialogTitle>
      <Formik
        initialValues={formData}
        validationSchema={validationSchema}
        onSubmit={(values, { setFieldValue }) =>
          handleSubmit(values, setFieldValue, false)
        }
        enableReinitialize
      >
        {({
          errors,
          touched,
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isValid,
        }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip Start Point"
                    name="startPoint"
                    type="text"
                    error={touched.startPoint && Boolean(errors.startPoint)}
                    helperText={touched.startPoint && errors.startPoint}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip Start Time"
                    name="tripStartTime"
                    type="time"
                    onChange={(e: any) => {
                      setFieldValue("tripStartTime", e.target.value);
                      e.target.blur();
                    }}
                    error={
                      touched.tripStartTime && Boolean(errors.tripStartTime)
                    }
                    helperText={touched.tripStartTime && errors.tripStartTime}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip Start Odometer"
                    name="tripStartOdometer"
                    type="number"
                    error={
                      touched.tripStartOdometer &&
                      Boolean(errors.tripStartOdometer)
                    }
                    helperText={
                      touched.tripStartOdometer && errors.tripStartOdometer
                    }
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip End Point"
                    name="endPoint"
                    type="text"
                    error={touched.endPoint && Boolean(errors.endPoint)}
                    helperText={touched.endPoint && errors.endPoint}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip End Time"
                    name="tripEndTime"
                    type="time"
                    onChange={(e: any) => {
                      setFieldValue("tripEndTime", e.target.value);
                      e.target.blur();
                    }}
                    error={touched.tripEndTime && Boolean(errors.tripEndTime)}
                    helperText={touched.tripEndTime && errors.tripEndTime}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Trip End Odometer"
                    name="tripEndOdometer"
                    type="number"
                    error={
                      touched.tripEndOdometer && Boolean(errors.tripEndOdometer)
                    }
                    helperText={
                      touched.tripEndOdometer && errors.tripEndOdometer
                    }
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    freeSolo
                    options={staffOptions}
                    loading={isLoading}
                    getOptionLabel={getOptionLabel}
                    value={selectedStaff}
                    onChange={(_, newValue, reason) => {
                      if (
                        reason === "selectOption" &&
                        typeof newValue === "object"
                      ) {
                        handleStaffChange(newValue, setFieldValue);
                      } else if (reason === "clear") {
                        handleStaffChange(null, setFieldValue);
                      }
                    }}
                    onInputChange={(_, inputValue, reason) => {
                      setSearchTerm(inputValue);

                      if (reason === "input") {
                        const matchingOption = staffOptions.find((option) =>
                          getOptionLabel(option)
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        );

                        if (!matchingOption && inputValue.trim() !== "") {
                          handleStaffChange(null, setFieldValue, inputValue);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Select Driver`}
                        placeholder={`Search from master data or enter manually...`}
                        required
                        fullWidth
                        error={!!searchError}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        {...commonTextFieldProps}
                      />
                    )}
                    renderOption={renderStaffOption}
                    noOptionsText={
                      isLoading
                        ? "Searching..."
                        : searchError
                        ? "Error loading options"
                        : `No matching Driver found. You can still type to add manually.`
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Number of Trips"
                    name="numberOfTrips"
                    type="number"
                    error={
                      touched.numberOfTrips && Boolean(errors.numberOfTrips)
                    }
                    helperText={touched.numberOfTrips && errors.numberOfTrips}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Date of Entry"
                    name="dateOfEntry"
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={touched.dateOfEntry && Boolean(errors.dateOfEntry)}
                    helperText={touched.dateOfEntry && errors.dateOfEntry}
                    required
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Remarks"
                    name="remarks"
                    type="text"
                    required={false}
                    error={touched.remarks && Boolean(errors.remarks)}
                    helperText={touched.remarks && errors.remarks}
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                sx={{ height: "30px", fontSize: "0.75rem" }}
                onClick={onClose}
              >
                Cancel
              </Button>
              {!editingLog && (
                <Button
                  sx={{ height: "30px", fontSize: "0.75rem" }}
                  onClick={() => {
                    if (isValid) {
                      handleSubmit(values, setFieldValue, true);
                    }
                  }}
                  variant="outlined"
                >
                  Add & Continue
                </Button>
              )}
              <Button
                sx={{ height: "30px", fontSize: "0.75rem" }}
                type="submit"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : editingLog ? "Update" : "Add"}
                {/* {editingLog ? "Update" : "Add"} */}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default LogActionModal;
